import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../store";
import { Link } from "react-router-dom";
import { IProfile } from "../../store/repIQPresetProfile/types";
import { ISocialMediaStatus } from "../../store/repIQSocialMediaStatus/types";
import { toggle } from "../../util/common";
import FAQ from "./FAQ";
import PresetProfile from "./PresetProfile";
import ResultChart from "./ResultChart";
import SocialChart from "./SocialChart";
import SocialMediaDropDown from "./SocialMedia/SocialMediaDropDown";
import RenderIcons from "../../Assets/css/_icons/partner_icon/RenderIcons";

const ReputationAnalyzer = (props: any) => {
    const SelectedNetwork: ISocialMediaStatus = useSelector(
        (state: ApplicationState) => state.repIQSelectedSocialMediaState && state.repIQSelectedSocialMediaState.selectedSocialMedia
    );
    const [selectedPresetProfile, setSelectedPresetProfile] = useState({} as IProfile);
    const [selectedNetwork, setSelectedNetwork] = useState({} as ISocialMediaStatus);
    const isLoading: boolean = useSelector((state: ApplicationState) => state.repIQSocialMediaStatusState && state.repIQSocialMediaStatusState.isLoading);
    const socialMediaStatusList: ISocialMediaStatus[] = useSelector(
        (state: ApplicationState) => state.repIQSocialMediaStatusState && state.repIQSocialMediaStatusState.socialMediaStatus
    );
    const [socialButterflyData, setSocialButterflyData] = useState(0);
    const [commandingLeaderData, setCommandingLeaderData] = useState(0);
    const [independentThinkerData, setIndependentThinkerData] = useState(0);
    const [sympatheticEarData, setSympatheticEarData] = useState(0);

    const updateSelectedNetwork = (network: ISocialMediaStatus) => {
        setSelectedNetwork(network);
        updateInteractionStyleSection(network);
    };

    const updateSelectedPresetProfile = (profile: IProfile) => {
        setSelectedPresetProfile(profile);
    };

    const updateInteractionStyleSection = (network: ISocialMediaStatus) => {
        setSocialButterflyData(Math.round((network.Extraversion * 100 + network.Agreeableness * 100) / 2));
        setCommandingLeaderData(Math.round((100 - network.EmotionalRange * 100 + (100 - network.Agreeableness * 100)) / 2));
        setIndependentThinkerData(Math.round((100 - network.Extraversion * 100 + (100 - network.Agreeableness * 100)) / 2));
        setSympatheticEarData(Math.round((network.EmotionalRange * 100 + network.Agreeableness * 100) / 2));
    };

    useEffect(() => {
        if (SelectedNetwork) updateSelectedNetwork(SelectedNetwork);
    }, [SelectedNetwork]);

    return (
        <React.Fragment>
            {isLoading ? (
                <div className="box loading"></div>
            ) : (
                <div className="box offset-padding">
                    <div className="box-header">
                        <div className="box-title-container offset-top-mobile">
                            <div className="box-title-icon" aria-hidden="true">
                                <RenderIcons iconName="users" />
                            </div>
                            <div className="box-title-text">
                                <h2 className="box-title">
                                    RepIQ<sup>&trade;</sup> Reputation Analyzer
                                </h2>
                            </div>
                        </div>
                        <div className="box-actions wrap-mobile">
                            {/*{socialMediaStatusList && socialMediaStatusList.length > 0 ?*/}
                            {/*    <Link to="/my-reputation/agreeableness" className="btn-default btn-primary-outline">*/}
                            {/*        <span className="btn-icon"><em className="las la-download"></em></span>*/}
                            {/*        <span className="btn-text">Download Report</span>*/}
                            {/*    </Link> :*/}
                            {/*    <button type="button" disabled={true} className="btn-default btn-primary-outline">*/}
                            {/*        <span className="btn-icon"><em className="las la-download"></em></span>*/}
                            {/*        <span className="btn-text">Download Report</span>*/}
                            {/*    </button>*/}
                            {/*}*/}
                        </div>
                    </div>
                    <div className="box-content">
                        <p>
                            The Reputation Analyzer continually assesses your activity on connected social media accounts and{" "}
                            <a href="my-reputation-data-sources" className="default_link">
                                other linked sources
                            </a>{" "}
                            to help you understand how your published content reflects your online reputation and personality.
                        </p>

                        <div className="line-divider margin-top-m margin-bottom-xs"></div>

                        <SocialMediaDropDown disableAction={socialMediaStatusList == null || socialMediaStatusList.length === 0} />

                        <div className="line-divider margin-top-xs"></div>
                        {socialMediaStatusList &&
                        socialMediaStatusList.length > 0 &&
                        selectedNetwork.MinimumWordCountFoundForAIProcess === true &&
                        selectedNetwork.IsPersonalityAnalysing === false ? (
                            <div id="container-online-reputation">
                                <div id="container-online-personality">
                                    <h3 className="content-sub-title">Your Online Personality</h3>

                                    <PresetProfile updateSelectedPresetProfile={updateSelectedPresetProfile} isSmallVersion={true} resetProfile={0} />

                                    <ResultChart
                                        title={"Agreeableness"}
                                        name={"agreeableness"}
                                        data={Math.round(selectedNetwork.Agreeableness * 100)}
                                        profileName={selectedPresetProfile.profileName}
                                        rangeHigh={selectedPresetProfile.categories?.Agreeableness?.high}
                                        rangeLow={selectedPresetProfile.categories?.Agreeableness?.low}
                                        tooltip={"Agreeableness describes your tendency to put the needs of others ahead of your own needs."}
                                        hasCTAButton={true}
                                    />

                                    <ResultChart
                                        title={"Conscientiousness"}
                                        name={"conscientiousness"}
                                        data={Math.round(selectedNetwork.Conscientiousness * 100)}
                                        profileName={selectedPresetProfile.profileName}
                                        rangeHigh={selectedPresetProfile.categories?.Conscientiousness?.high}
                                        rangeLow={selectedPresetProfile.categories?.Conscientiousness?.low}
                                        tooltip={"Conscientiousness describes your tendency to focus on achieving your goals."}
                                        hasCTAButton={true}
                                    />

                                    <ResultChart
                                        title={"Emotional Range"}
                                        name={"emotional-range"}
                                        data={Math.round(selectedNetwork.EmotionalRange * 100)}
                                        profileName={selectedPresetProfile.profileName}
                                        rangeHigh={selectedPresetProfile.categories?.EmotionalRange?.high}
                                        rangeLow={selectedPresetProfile.categories?.EmotionalRange?.low}
                                        tooltip={"Emotional Range describes your susceptibility to stress and negative emotions."}
                                        hasCTAButton={true}
                                    />

                                    <ResultChart
                                        title={"Openness to Experiences"}
                                        name={"openness-to-experiences"}
                                        data={Math.round(selectedNetwork.OpennessToExperiences * 100)}
                                        profileName={selectedPresetProfile.profileName}
                                        rangeHigh={selectedPresetProfile.categories?.OpennessToExperiences?.high}
                                        rangeLow={selectedPresetProfile.categories?.OpennessToExperiences?.low}
                                        tooltip={"Openness to Experiences describes your tendency to try new things and think in abstract ways."}
                                        hasCTAButton={true}
                                    />

                                    <ResultChart
                                        title={"Extraversion"}
                                        name={"extraversion"}
                                        data={Math.round(selectedNetwork.Extraversion * 100)}
                                        profileName={selectedPresetProfile.profileName}
                                        rangeHigh={selectedPresetProfile.categories?.Extraversion?.high}
                                        rangeLow={selectedPresetProfile.categories?.Extraversion?.low}
                                        tooltip={"Extraversion describes your tendency to be energized by social interactions."}
                                        hasCTAButton={true}
                                    />

                                    <div className="personality-legend">
                                        <ul className="list-reset list-inline text-align-center">
                                            <li className="margin-right-m">
                                                <span className="key-score"></span> Your Score out of 100
                                            </li>
                                            <li className="profile-benchmark" style={{ display: "none" }}>
                                                <span className="key-profile"></span> Preset Profile Benchmark
                                            </li>
                                        </ul>
                                    </div>

                                    <div className="margin-bottom-xl"></div>
                                </div>
                                <div id="container-interaction-style" className="margin-bottom-l">
                                    <h3 className="content-sub-title">Your Interaction Style</h3>
                                    <p className="small_text">
                                        The graphs below represent the four styles of socializing and developing relationships with others. Higher percentages
                                        indicate a stronger pattern between your online behaviour and the interaction style, while lower percentages indicate weaker
                                        patterns.
                                    </p>

                                    <div className="row margin-top-m">
                                        <div className="col-xs-6 col-no-gutter">
                                            <SocialChart title={"Social Butterfly"} color={"#5DBEAA"} data={socialButterflyData} sequence={1} />
                                        </div>
                                        <div className="col-xs-6 col-no-gutter">
                                            <SocialChart title={"Sympathetic Ear"} color={"#306E91"} data={sympatheticEarData} sequence={2} />
                                        </div>
                                    </div>
                                    <div className="row margin-top-xl">
                                        <div className="col-xs-6 col-no-gutter">
                                            <SocialChart title={"Commanding Leader"} color={"#FFA126"} data={commandingLeaderData} sequence={3} />
                                        </div>
                                        <div className="col-xs-6 col-no-gutter">
                                            <SocialChart title={"Independent Thinker"} color={"#FE7676"} data={independentThinkerData} sequence={4} />
                                        </div>
                                    </div>

                                    <div className="margin-vertical-xl"></div>

                                    <div className="drawer-container drawer-container-alt">
                                        <div className="drawer-header">
                                            <button
                                                aria-expanded="false"
                                                className="btn-default btn-primary btn-toggle btn-drawer btn-drawer-alt"
                                                id="btn-interaction-styles"
                                                onClick={() => toggle("interaction-styles", "btn-interaction-styles")}
                                            >
                                                <span className="btn-toggle-status sr-only">Show</span>
                                                <span className="btn-text">
                                                    <span className="inline-drawer-icon">
                                                        <RenderIcons iconName="user-alt" />
                                                        <em className="las la-exchange-alt"></em>
                                                        <RenderIcons iconName="user-alt" />
                                                    </span>
                                                    Interaction Style Details
                                                </span>
                                            </button>
                                        </div>

                                        <div className="drawer-content" id="interaction-styles" style={{ display: "none" }} aria-hidden="true">
                                            <div className="padding-vertical-xs padding-horizontal-xs">
                                                <div style={{ padding: "5px" }}>
                                                    <div className="row">
                                                        <p>
                                                            The graphs show four styles of socializing and developing relationships with others. Higher percentages
                                                            indicate a stronger pattern between your online behavior and the interaction style, while lower
                                                            percentages indicate weaker patterns.
                                                        </p>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-sm-6" style={{ paddingTop: "11px", paddingRight: "1px" }}>
                                                            <strong>Social Butterfly</strong>
                                                        </div>
                                                        <div className="col-sm-6">
                                                            {socialButterflyData > 65 && (
                                                                <div className="Rectangle">
                                                                    <label className="STRONG-MATCH">STRONG MATCH</label>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <p>
                                                            <label style={{ fontStyle: "italic" }}>The Social Butterfly</label> is viewed as friendly, outgoing, and
                                                            charming. They easily initiate conversation and enjoy social interaction.
                                                        </p>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-sm-6" style={{ paddingTop: "11px", paddingRight: "1px" }}>
                                                            <strong>Sympathetic Ear</strong>
                                                        </div>
                                                        <div className="col-sm-6">
                                                            {sympatheticEarData > 65 && (
                                                                <div className="Rectangle">
                                                                    <label className="STRONG-MATCH">STRONG MATCH</label>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="row">
                                                        <p>
                                                            <label style={{ fontStyle: "italic" }}>The Sympathetic Ear</label> is seen as gentle, kind, and
                                                            reserved. They value close, nurturing relationships in their social lives.
                                                        </p>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-sm-7" style={{ paddingTop: "11px", paddingRight: "1px" }}>
                                                            <strong>Commanding Leader</strong>
                                                        </div>
                                                    </div>
                                                    {commandingLeaderData > 65 && (
                                                        <div className="col-sm-5">
                                                            <div className="Rectangle">
                                                                <label className="STRONG-MATCH">STRONG MATCH</label>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="row">
                                                        <p>
                                                            <label style={{ fontStyle: "italic" }}>The Commanding Leader</label> takes charge of situations. Their
                                                            interaction style is often viewed as forceful, direct, and dominant.
                                                        </p>
                                                    </div>
                                                    <div className="row">
                                                        <div className="col-sm-7" style={{ paddingTop: "11px", paddingRight: "1px" }}>
                                                            <strong>Independent Thinker</strong>
                                                        </div>
                                                    </div>
                                                    {independentThinkerData > 65 && (
                                                        <div className="col-sm-5">
                                                            <div className="Rectangle">
                                                                <label className="STRONG-MATCH">STRONG MATCH</label>
                                                            </div>
                                                        </div>
                                                    )}
                                                    <div className="row">
                                                        <p>
                                                            <label style={{ fontStyle: "italic" }}>The Independent Thinker</label> is analytical, and often seen as
                                                            detached for their preferred solitude. They value close relationships with a select group.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="box">
                                <div className="box-content text-align-center" style={{ minHeight: "200px", backgroundColor: "#F2F6F9" }}>
                                    <div style={{ minHeight: "90px" }} />

                                    {socialMediaStatusList && socialMediaStatusList.length === 0 ? (
                                        <Link to="/my-reputation-data-sources" className="btn-default btn-primary">
                                            <span className="btn-text">Connect to Social Media Accounts</span>
                                        </Link>
                                    ) : selectedNetwork.IsPersonalityAnalysing === true ? (
                                        <div className="box-content text-align-center">
                                            <p>Allow a few minutes for RepIQ to create your profile.</p>
                                        </div>
                                    ) : (
                                        selectedNetwork.MinimumWordCountFoundForAIProcess === false && (
                                            <div className="box-content text-align-center">
                                                <p>
                                                    RepIQ requires posted content from a social media account to contain at least 100 words.
                                                    <br />
                                                    For more accurate analysis, connect a social media account with posts containing at least 600 words.
                                                </p>
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="line-divider margin-bottom-l"></div>
                        <FAQ />
                        <div className="margin-bottom-xl"></div>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};
export default ReputationAnalyzer;
